import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Upload,
  message,
  Radio,
  DatePicker,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { updateSimpleBanner, createCarouselBanner } from "../../../../api/banner";
import { uploadImage } from "../../../../api/service";
import colors from "../../../../share/constants/constants";
import LocationPicker from "../../../../components/common/LocationPicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const CarouselBannerForm = ({ reloadBanners, formData, isEdit }) => {
  const [form] = Form.useForm();
  const [clickAction, setClickAction] = useState(formData?.clickAction || "service");
  const [imageUrl, setImageUrl] = useState(formData?.imageUrl || "");
  const [imageUrlWeb, setImageUrlWeb] = useState(formData?.imageUrlWeb || "");
  const [loading, setLoading] = useState(false);
  const [locationCoordinates, setLocationCoordinates] = useState(
    formData?.locationCoordinates || null
  );
  const [locationName, setLocationName] = useState(
    formData?.locationName || ""
  );

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    console.log(file.type , "type")
    const isLt1M = file.size / 1024 / 1024 < 1;
    
    // Check image dimensions and aspect ratio
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const { width, height } = img;
        const aspectRatio = width / height;
        const targetAspectRatio = 1.89;
        const aspectRatioTolerance = 0.05; // Allow small tolerance
        
        const isValidDimensions = width === 680 && height === 360;
        const isValidAspectRatio = Math.abs(aspectRatio - targetAspectRatio) <= aspectRatioTolerance;
        
        resolve({ 
          isJpgOrPng, 
          isLt1M, 
          isValidDimensions, 
          isValidAspectRatio,
          width,
          height,
          aspectRatio: aspectRatio.toFixed(2)
        });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageUpload = async (event) => {
    try {
      const validImage = await beforeUpload(event.file);

      if (!validImage.isJpgOrPng) {
        return message.error("You can only upload JPG/PNG file!");
      } else if (!validImage.isLt1M) {
        return message.error("Image must be smaller than 1MB!");
      } else if (!validImage.isValidDimensions) {
        return message.error(`Image dimensions must be exactly 680x360 pixels. Current: ${validImage.width}x${validImage.height}`);
      } else if (!validImage.isValidAspectRatio) {
        return message.error(`Image aspect ratio must be 1.89:1. Current: ${validImage.aspectRatio}:1`);
      } else if (event.file && validImage.isJpgOrPng && validImage.isLt1M && validImage.isValidDimensions && validImage.isValidAspectRatio) {
        const formData = new FormData();
        formData.append("image", event.file);
        const response = await uploadImage(formData);
        if (response) {
          setImageUrl(response.url);
          form.setFieldValue("imageUrl", response.url);
          message.success("Image uploaded successfully");
        }
      }
    } catch (error) {
      console.log(error);
      message.error("Error while uploading image");
    }
  };

  const handleWebImageUpload = async (event) => {
    try {
      const validImage = await beforeUpload(event.file);

      if (!validImage.isJpgOrPng) {
        return message.error("You can only upload JPG/PNG file!");
      } else if (!validImage.isLt1M) {
        return message.error("Image must be smaller than 1MB!");
      } else if (event.file && validImage.isJpgOrPng && validImage.isLt1M) {
        const formData = new FormData();
        formData.append("image", event.file);
        const response = await uploadImage(formData);
        if (response) {
          setImageUrlWeb(response.url);
          form.setFieldValue("imageUrlWeb", response.url);
          message.success("Web image uploaded successfully");
        }
      }
    } catch (error) {
      console.log(error);
      message.error("Error while uploading web image");
    }
  };

  useEffect(() => {
    if (formData?.locationCoordinates) {
      setLocationCoordinates(formData.locationCoordinates);
    }
    if (formData?.locationName) {
      setLocationName(formData.locationName);
    }
  }, [formData]);

  useEffect(() => {
    if (locationCoordinates) {
      form.setFieldsValue({
        locationCoordinates: locationCoordinates,
      });
    }
    if (locationName) {
      form.setFieldsValue({
        locationName: locationName,
      });
    }
  }, [locationCoordinates, locationName, form]);

  const handleClickActionChange = (e) => {
    setClickAction(e.target.value);
    // Clear related fields when changing click action
    if (e.target.value === "web-link") {
      form.setFieldsValue({ serviceId: undefined });
    } else if (e.target.value === "service") {
      form.setFieldsValue({ link: undefined });
    }
  };

  const onFinish = async (values) => {
    // For edit mode, use existing images if no new ones uploaded
    const finalImageUrl = imageUrl || formData?.imageUrl;
    const finalImageUrlWeb = imageUrlWeb || formData?.imageUrlWeb;
    
    if (!finalImageUrl) {
      message.error("Please upload a main image");
      return;
    }
    
    if (!finalImageUrlWeb) {
      message.error("Please upload a web image");
      return;
    }

    setLoading(true);
    try {
      const formattedEndDate = values.endDate
        ? dayjs(values.endDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
        : null;
      const payload = {
        ...values,
        imageUrl: finalImageUrl,
        imageUrlWeb: finalImageUrlWeb,
        position: "first", // Hardcoded as requested
        bannerType: "simple", // Hardcoded as requested
        clickAction: values.clickAction,
        endDate: formattedEndDate,
        locationCoordinates: locationCoordinates,
        locationName: locationName,
      };

      // Handle click action specific fields
      if (values.clickAction === "web-link") {
        payload.link = values.link;
        // Clear other fields
        delete payload.serviceId;
      } else if (values.clickAction === "service") {
        payload.serviceId = values.serviceId;
        // Clear other fields
        delete payload.link;
      }

      let response;
      if (isEdit && formData?._id) {
        response = await updateSimpleBanner(formData._id, payload);
      } else {
        response = await createCarouselBanner(payload);
      }

      if (response) {
        message.success(`Banner ${isEdit ? "updated" : "created"} successfully!`);
        if (!isEdit) {
          form.resetFields();
          setImageUrl("");
          setImageUrlWeb("");
          setLocationCoordinates(null);
          setLocationName("");
        }
        reloadBanners && reloadBanners();
      }
    } catch (error) {
      console.error(`Error ${isEdit ? "updating" : "creating"} banner:`, error);
      message.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const renderClickActionFields = () => {
    switch (clickAction) {
      case "web-link":
        return (
          <Form.Item
            name="link"
            label="Link URL"
            rules={[
              {
                required: true,
                message: "Please enter the link URL",
              },
              {
                type: "url",
                message: "Please enter a valid URL",
              },
            ]}
          >
            <Input placeholder="Enter link URL (e.g., https://example.com)" />
          </Form.Item>
        );

      case "service":
        return (
          <Form.Item
            name="serviceId"
            label="Service ID"
            rules={[
              {
                required: true,
                message: "Please enter the service ID",
              },
            ]}
          >
            <Input placeholder="Enter service ID" />
          </Form.Item>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">
        {isEdit ? "Edit Carousel Banner" : "Create New Carousel Banner"}
      </h3>
      
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          ...formData,
          clickAction: formData?.clickAction || "service",
          // Handle serviceId for edit case - extract _id if it's populated
          serviceId: formData?.serviceId?._id || formData?.serviceId,
          endDate: formData?.endDate ? dayjs(formData.endDate) : undefined,
          locationCoordinates: formData?.locationCoordinates || null,
          locationName: formData?.locationName || "",
        }}
        onFinish={onFinish}
      >
        <Form.Item
          label="Main Image (Required)"
          name="imageUrl"
          rules={[
            {
              required: true,
              message: "Please upload a main image",
            },
          ]}
          extra="Required dimensions: 680x360 pixels (1.89:1 aspect ratio). Max size: 1MB."
        >
          <Upload
            listType="picture-card"
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleImageUpload}
            accept="image/jpeg,image/png"
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="Main banner"
                style={{ width: "100%" }}
              />
            ) : (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Upload Main Image</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item
          label="Web Image (Required)"
          name="imageUrlWeb"
          rules={[
            {
              required: true,
              message: "Please upload a web image",
            },
          ]}
        >
          <Upload
            listType="picture-card"
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleWebImageUpload}
            accept="image/jpeg,image/png"
          >
            {imageUrlWeb ? (
              <img
                src={imageUrlWeb}
                alt="Web banner"
                style={{ width: "100%" }}
              />
            ) : (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Upload Web Image</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item
          label="Location"
          name="location"
          rules={[
            {
              required: false,
            },
          ]}
        >
          <LocationPicker
            value={{
              coordinates: locationCoordinates,
              locationName: locationName,
            }}
            onChange={(coordinates) => {
              setLocationCoordinates(coordinates);
              form.setFieldValue("locationCoordinates", coordinates);
            }}
            onLocationNameChange={(name) => {
              setLocationName(name);
              form.setFieldValue("locationName", name);
            }}
          />
        </Form.Item>

        <Form.Item 
          label="Click Action" 
          name="clickAction"
          rules={[
            {
              required: true,
              message: "Please select click action",
            },
          ]}
        >
          <Radio.Group onChange={handleClickActionChange} value={clickAction}>
            <Radio value="service">Service</Radio>
            <Radio value="web-link">Web Link</Radio>
          </Radio.Group>
        </Form.Item>

        {renderClickActionFields()}

        <Form.Item name="endDate" label="End Date">
          <DatePicker
            placeholder="Select end date and time"
            format="YYYY-MM-DD HH:mm:ss"
            showTime={{
              defaultValue: dayjs("00:00:00", "HH:mm:ss"),
            }}
            className="w-full"
          />
        </Form.Item>

        <Form.Item>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading}
            style={{ backgroundColor: colors.primaryPurple, borderColor: colors.primaryPurple }}
            className="w-full"
          >
            {isEdit ? "Update Banner" : "Create Banner"}
          </Button>
        </Form.Item>
      </Form>

             {/* Preview Section */}
       {imageUrl && (
         <div className="m2-6">
           <h4 className="text-md font-medium mb-3">Mobile Image</h4>
           <div className="bg-gray-100 rounded-lg">
             <img
               src={imageUrl}
               alt="Banner Preview"
               style={{ width: "100%", maxWidth: "500px", height: "auto" }}
               className="rounded"
             />
           </div>
         </div>
       )}

      {/* Preview Section */}
      {imageUrlWeb && (
        <div className="mt-2">
          <h4 className="text-md font-medium mb-3">Web Image</h4>
          <div className="bg-gray-100 rounded-lg">
            <img
              src={imageUrlWeb}
              alt="Banner Preview"
              style={{ width: "100%", maxWidth: "500px", height: "auto" }}
              className="rounded"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CarouselBannerForm;
